import React from "react";
import { useAgents } from "../../common/hooks/useAgents";
import type { Agent } from "../../common/types/Agent";
import { excludeUndefined } from "../../common/utils/array.util";
import { AgentAvatar } from "./Avatar";
import Dropdown, { type DropdownOption } from "./Dropdown";

const filterDisabledAgentUnlessAssigned = (
    agent: Agent,
    assignedAgentId: string | null | undefined,
) => !agent.disabled || (agent.disabled && assignedAgentId === agent.id);

const buildAssignableAgents = (
    agents: Agent[],
    assignedAgentId: string | null | undefined,
    currentAgentId: string | null | undefined,
    useUnassigned: boolean,
    includeAdmins: boolean,
): DropdownOption[] => {
    const currentAgent = agents.find((it) => it.id === currentAgentId);
    const unassigned = useUnassigned
        ? {
              value: null,
              label: "Unassigned",
              image: <AgentAvatar agentId="unassigned" size="small" />,
          }
        : undefined;
    const assignableAgents: DropdownOption[] = agents
        .filter((agent) => agent.role === "agent" || includeAdmins)
        .filter((agent) => filterDisabledAgentUnlessAssigned(agent, assignedAgentId))
        .map((agent) => ({
            value: agent.id,
            label: agent.name,
            image: <AgentAvatar agentId={agent.id} size="small" />,
        }));

    if (currentAgent) {
        return [
            unassigned,
            {
                value: currentAgent.id,
                label: currentAgent.name,
                image: <AgentAvatar agentId={currentAgent.id} size="small" />,
            },
            ...assignableAgents.filter((it) => it.value !== currentAgent.id),
        ].filter(excludeUndefined);
    }

    return [unassigned, ...assignableAgents].filter(excludeUndefined);
};

type Props = {
    assignedAgentId: string | null | undefined;
    currentAgentId: string | null | undefined;
    className?: string;
    label?: string;
    useUnassigned?: boolean;
    includeAdmins?: boolean;
    onChange: (
        agentId: string | null,
        agentFirstName: string | null | undefined,
        agentName: string | null,
    ) => void;
};

const AgentDropdown = function AgentDropdown(props: Props) {
    const {
        className,
        assignedAgentId,
        currentAgentId,
        label,
        onChange,
        useUnassigned = true,
        includeAdmins = false,
    } = props;

    const agents = useAgents();

    return (
        <Dropdown
            className={className}
            label={label}
            optionList={buildAssignableAgents(
                agents,
                assignedAgentId,
                currentAgentId,
                useUnassigned,
                includeAdmins,
            )}
            currentValue={assignedAgentId}
            onChange={(value) => {
                const agent = agents.find((a) => a.id === value);
                if (agent) {
                    onChange(agent.id, agent.firstName, agent.name);
                } else {
                    onChange(null, null, null);
                }
            }}
        />
    );
};

export default AgentDropdown;
